import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { TrendingUp, Camera, DollarSign, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import BookingModal from '@/components/BookingModal';
import SEO from '@/components/SEO';
import { SmsOptInCheckbox } from '@/components/SmsConsentDisclosure';

const SellHome = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [sellerForm, setSellerForm] = useState({
    name: '',
    email: '',
    phone: '',
    property_address: '',
    property_type: '',
    bedrooms: '',
    bathrooms: '',
    square_feet: '',
    timeline: '',
    reason_for_selling: '',
    current_mortgage: false,
    additional_info: '',
    sms_opt_in: false,
  });

  const handleFormChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSellerForm({ ...sellerForm, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/seller-inquiry', sellerForm);
      toast.success('Your inquiry has been submitted! We\'ll contact you soon with a valuation.');
      setSellerForm({
        name: '',
        email: '',
        phone: '',
        property_address: '',
        property_type: '',
        bedrooms: '',
        bathrooms: '',
        square_feet: '',
        timeline: '',
        reason_for_selling: '',
        current_mortgage: false,
        additional_info: ''
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  return (
    <div className="min-h-screen" data-testid="sell-home-page">
      <SEO 
        title="Sell Your Home | Get Top Dollar in Memphis, TN & North MS"
        description="Ready to sell your home in Memphis, Southaven, Olive Branch, or surrounding areas? Bespoke Realty provides strategic marketing, expert pricing, and personalized service to get you the best value."
        keywords="sell my home Memphis, list house Tennessee, sell property Mississippi, home value Memphis, Southaven real estate agent, selling a house Olive Branch"
      />
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/ny17qz0f_Sell.png)',
          }}
        ></div>
        <div className="relative z-20 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 text-white" data-testid="sell-heading">Sell Your Home</h1>
          <p className="text-xl text-white">Get the best value with our strategic marketing approach</p>
        </div>
      </section>

      {/* Selling Process - Right after Hero */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">The Selling Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-bold mb-2">Valuation</h3>
              <p className="text-gray-600">Comprehensive market analysis to determine optimal pricing</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-bold mb-2">Preparation</h3>
              <p className="text-gray-600">Professional staging and photography</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-bold mb-2">Marketing</h3>
              <p className="text-gray-600">Strategic promotion to qualified buyers</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-xl font-bold mb-2">Closing</h3>
              <p className="text-gray-600">Expert negotiation and smooth transaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Seller Intake Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6" data-testid="seller-form-heading">Request a Home Valuation</h2>
            <p className="text-gray-600 mb-8">Provide us with your property details, and we'll prepare a comprehensive market analysis.</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={sellerForm.name}
                    onChange={handleFormChange}
                    required
                    data-testid="seller-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={sellerForm.email}
                    onChange={handleFormChange}
                    required
                    data-testid="seller-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={sellerForm.phone}
                    onChange={handleFormChange}
                    required
                    data-testid="seller-phone-input"
                  />
                  <SmsOptInCheckbox
                    checked={sellerForm.sms_opt_in}
                    onChange={(v) => setSellerForm({ ...sellerForm, sms_opt_in: v })}
                  />
                </div>
                <div>
                  <Label htmlFor="property_address">Property Address *</Label>
                  <Input
                    id="property_address"
                    name="property_address"
                    value={sellerForm.property_address}
                    onChange={handleFormChange}
                    required
                    data-testid="seller-address-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="property_type">Property Type *</Label>
                  <Select name="property_type" value={sellerForm.property_type} onValueChange={(value) => setSellerForm({...sellerForm, property_type: value})}>
                    <SelectTrigger data-testid="seller-type-select">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="townhouse">Townhouse</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="timeline">Timeline *</Label>
                  <Select name="timeline" value={sellerForm.timeline} onValueChange={(value) => setSellerForm({...sellerForm, timeline: value})}>
                    <SelectTrigger data-testid="seller-timeline-select">
                      <SelectValue placeholder="When are you looking to sell?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediately">Immediately</SelectItem>
                      <SelectItem value="1-3-months">1-3 months</SelectItem>
                      <SelectItem value="3-6-months">3-6 months</SelectItem>
                      <SelectItem value="6-12-months">6-12 months</SelectItem>
                      <SelectItem value="just-exploring">Just exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    name="bedrooms"
                    type="number"
                    min="1"
                    value={sellerForm.bedrooms}
                    onChange={handleFormChange}
                    data-testid="seller-bedrooms-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bathrooms">Bathrooms</Label>
                  <Input
                    id="bathrooms"
                    name="bathrooms"
                    type="number"
                    step="0.5"
                    min="1"
                    value={sellerForm.bathrooms}
                    onChange={handleFormChange}
                    data-testid="seller-bathrooms-input"
                  />
                </div>
                <div>
                  <Label htmlFor="square_feet">Square Feet</Label>
                  <Input
                    id="square_feet"
                    name="square_feet"
                    type="number"
                    value={sellerForm.square_feet}
                    onChange={handleFormChange}
                    data-testid="seller-sqft-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="reason_for_selling">Reason for Selling</Label>
                <Input
                  id="reason_for_selling"
                  name="reason_for_selling"
                  value={sellerForm.reason_for_selling}
                  onChange={handleFormChange}
                  placeholder="e.g., Relocating, Downsizing, Upgrading"
                  data-testid="seller-reason-input"
                />
              </div>

              <div>
                <Label htmlFor="additional_info">Additional Information</Label>
                <Textarea
                  id="additional_info"
                  name="additional_info"
                  value={sellerForm.additional_info}
                  onChange={handleFormChange}
                  rows={4}
                  placeholder="Any recent upgrades, special features, or other details?"
                  data-testid="seller-additional-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="current_mortgage"
                  name="current_mortgage"
                  checked={sellerForm.current_mortgage}
                  onChange={handleFormChange}
                  className="h-4 w-4"
                  data-testid="seller-mortgage-checkbox"
                />
                <Label htmlFor="current_mortgage" className="font-normal">I currently have a mortgage on this property</Label>
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                data-testid="seller-submit-btn"
              >
                Request Free Valuation
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* How Bespoke Markets Differently */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">How Bespoke Markets Your Home Differently</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Camera className="h-10 w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Professional Photography</h3>
              <p className="text-gray-600">High-quality photos and virtual tours that showcase your property's best features.</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <TrendingUp className="h-10 w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Strategic Pricing</h3>
              <p className="text-gray-600">Data-driven pricing strategies to maximize your return while attracting qualified buyers.</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Users className="h-10 w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Multi-Channel Marketing</h3>
              <p className="text-gray-600">Your listing reaches buyers through MLS, social media, email campaigns, and our network.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#BCA480] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Sell Your Home?</h2>
          <p className="text-xl mb-8">Schedule a free consultation to discuss your property and selling goals.</p>
          <Button 
            size="lg" 
            className="bg-white text-[#434345] hover:bg-gray-100 px-8"
            onClick={() => setShowBooking(true)}
            data-testid="seller-cta-btn"
          >
            Schedule Free Consultation
          </Button>
        </div>
      </section>

      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} bookingType="valuation" />
    </div>
  );
};

export default SellHome;
